import { SubscriptionDetailsResponse, TaskResponse } from "@9amhealth/openapi";
import type { SanityImageAssetDocument } from "@sanity/client";
import { createCmsClient } from "@9amhealth/cms-api";
import { Cubit } from "blac";
import { Blac } from "blac-next";
import type { SanityResolvedDocument } from "src/api/SanityService";
import { SUBSCRIPTION_PLAN_ID_CDCDPP } from "src/constants/misc";
import { KnownProgram } from "src/constants/programs";
import { logSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import envVariables from "src/lib/envVariables";
import leftPadNumber from "src/lib/leftPadNumber";
import reportErrorSentry from "src/lib/reportErrorSentry";
import type {
  AppTaskDetails,
  TaskResponseKnown
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import TaskManagementBloc from "src/state/TaskManagementBloc/TaskManagementBloc";
import { ProfileProgramMembership } from "src/constants/programs";
import { subscriptionState, userState } from "src/state/state";

export type SelectModuleOption = "next-available" | "next-date";

interface ProgramModuleRelatedData {
  key?: string | "videoId";
  value?: string;
  _key: string;
}

export interface ProgramModuleItem {
  title?: string;
  status?: TaskResponse.status;
  moduleNumber?: string;
  description?: string;
  taskReference?: string;
  image?: SanityImageAssetDocument;
  relatedData?: ProgramModuleRelatedData[];
  tasks?: TaskResponseKnown[];
  _key: string;
}

export interface ProgramContent {
  // title, description, slug, nextVideoTitle, image
  title?: string;
  description?: string;
  detailText?: string;
  slug?: string;
  image?: SanityImageAssetDocument;
  attachments?: {
    file?: SanityResolvedDocument;
    name?: string;
    description?: string;
    _key: string;
  }[];
  moduleItems?: ProgramModuleItem[];
}

interface ProgramBlocState {
  userHasNoPrograms: true | undefined;
  programs: Set<string>;
  autoProgramDetected?: KnownProgram;
  /** `null` means the CMS has no doc for the program; `undefined` means not loaded yet. */
  programContent: Record<string, ProgramContent | null | undefined>;
  programStatus: Record<string, ProgramStatus | undefined>;
}

interface ProgramModuleItemSanityResponse {
  description?: string;
  image?: SanityImageAssetDocument;
  title?: string;
  relatedData?: ProgramModuleRelatedData[];
  taskReference?: string;
  _key: string;
}

interface ProgramContentSanityResponse {
  description?: string;
  title?: string;
  detailText?: string;
  language?: string;
  slug?: {
    current?: string;
  };
  image?: SanityImageAssetDocument;
  moduleItems?: ProgramModuleItemSanityResponse[];
  attachments?: {
    file?: SanityResolvedDocument;
    name?: string;
    description?: string;
    _key: string;
  }[];
}

export {
  KnownGroup,
  KnownProgram,
  KnownProgramInfo
} from "src/constants/programs";
import { onUserSessionReset } from "src/lib/onUserSessionReset";

export enum ProgramStatus {
  COMPLETED = "completed",
  IN_PROGRESS = "in_progress",
  AVAILABLE = "available"
}

export default class ProgramBloc extends Cubit<ProgramBlocState> {
  constructor() {
    super({
      userHasNoPrograms: undefined,
      programs: new Set(),
      programContent: {},
      programStatus: {}
    });

    onUserSessionReset(() => {
      this.emit({
        userHasNoPrograms: undefined,
        programs: new Set(),
        programContent: {},
        programStatus: {}
      });
    });
  }

  updatePrograms = async ({
    loadSubscriptions
  }: {
    loadSubscriptions?: boolean;
  } = {}): Promise<void> => {
    if (loadSubscriptions) {
      await subscriptionState.loadAllSubscriptions();
    }

    // get all active subscriptions
    const activeActiveSubscriptions = subscriptionState.filterAllSubscriptions({
      status: [SubscriptionDetailsResponse.status.ACTIVE],
      looseCheck: true
    });

    // Determine active programs from subscription plan IDs
    const activePrograms = activeActiveSubscriptions.reduce<Set<string>>(
      (acc, subscription) => {
        if (subscription.planId === SUBSCRIPTION_PLAN_ID_CDCDPP) {
          acc.add(KnownProgram.CDC_DPP);
        }
        return acc;
      },
      new Set()
    );

    // update state
    this.emit({
      ...this.state,
      programs: activePrograms
    });
  };

  static parseProgramContentFormSanityResponse = async (
    responseContent: ProgramContentSanityResponse
  ): Promise<ProgramContent> => {
    return {
      ...responseContent,
      slug: responseContent.slug?.current ?? "",
      moduleItems: responseContent.moduleItems?.map((moduleItem, index) => {
        const paddedIndex = leftPadNumber(
          index + 1,
          `${responseContent.moduleItems?.length}`.length
        );
        return {
          ...moduleItem,
          moduleNumber: paddedIndex
        } satisfies ProgramModuleItem;
      })
    };
  };

  static loadProgramMediaContent = async (
    program: KnownProgram
  ): Promise<ProgramContent | null | undefined> => {
    try {
      const cmsClient = createCmsClient(envVariables.CMS_API_BASE_URL);
      const res = await cmsClient.api.content.program.$get({
        query: { slug: program }
      });
      if (!res.ok) {
        throw new Error(`program content request failed: ${res.status}`);
      }
      const { doc } = (await res.json()) as {
        doc: ProgramContentSanityResponse | null;
      };
      return doc
        ? await ProgramBloc.parseProgramContentFormSanityResponse(doc)
        : null;
    } catch (e) {
      reportErrorSentry(e);
      return undefined;
    }
  };

  loadProgramTasks = async (
    program: KnownProgram
  ): Promise<TaskResponseKnown[] | undefined> => {
    try {
      const taskManagementState = Blac.getBloc(TaskManagementBloc);
      return await taskManagementState.loadProgramTasks(program);
    } catch (error) {
      reportErrorSentry(error);
    }
  };

  addTasksToProgramContent = (
    content: ProgramContent | undefined,
    tasks: TaskResponseKnown[]
  ): ProgramContent | undefined => {
    if (!content) {
      return;
    }

    const moduleItems: ProgramModuleItem[] | undefined =
      content.moduleItems?.map((moduleItem) => {
        const moduleItemTasks = tasks.filter(
          (task) => task.group === moduleItem.taskReference
        );

        let moduleStatus: TaskResponse.status | undefined;

        if (
          // if all tasks are completed, set module status to completed
          moduleItemTasks.every(
            (task) => task.status === TaskResponse.status.COMPLETED
          )
        ) {
          moduleStatus = TaskResponse.status.COMPLETED;
        } else if (
          // if all are LOCKED, set module status to LOCKED
          moduleItemTasks.every(
            (task) => task.status === TaskResponse.status.LOCKED
          )
        ) {
          moduleStatus = TaskResponse.status.LOCKED;
        } else if (
          // if any of the tasks are IN_PROGRESS or AVAILABLE, set module status to IN_PROGRESS
          moduleItemTasks.some(
            (task) =>
              task.status === TaskResponse.status.IN_PROGRESS ||
              task.status === TaskResponse.status.AVAILABLE
          )
        ) {
          moduleStatus = TaskResponse.status.IN_PROGRESS;
        } else if (
          // if any tasks are SKIPPED, set module status to SKIPPED
          moduleItemTasks.some(
            (task) => task.status === TaskResponse.status.SKIPPED
          )
        ) {
          moduleStatus = TaskResponse.status.SKIPPED;
        }

        return {
          ...moduleItem,
          tasks: moduleItemTasks,
          status: moduleStatus
        } satisfies ProgramModuleItem;
      });

    return {
      ...content,
      moduleItems
    };
  };

  loadingPrograms: Record<string, Promise<void> | undefined> = {};
  loadProgramContent = (
    program: KnownProgram,
    forceUpdate?: boolean
  ): Promise<void> => {
    if (!forceUpdate && this.state.programContent[program] !== undefined) {
      return Promise.resolve();
    }

    if (this.loadingPrograms[program]) {
      return this.loadingPrograms[program];
    }

    const promise = (async () => {
      try {
        const responses = await Promise.all([
          ProgramBloc.loadProgramMediaContent(program),
          this.loadProgramTasks(program)
        ]);

        let content = responses[0];
        const tasks = responses[1];

        if (content && tasks) {
          content = this.addTasksToProgramContent(content, tasks);
        }

        this.emit({
          ...this.state,
          programContent: {
            ...this.state.programContent,
            [program]: content
          }
        });
      } catch (error) {
        logSentryBreadcrumb(
          "errorHandler",
          "error while fetching program content",
          error
        );
      } finally {
        this.loadingPrograms[program] = undefined;
      }
    })();

    this.loadingPrograms[program] = promise;
    return promise;
  };

  getProgramContent = (program?: KnownProgram): ProgramContent => {
    const fallback = {
      attachments: [],
      moduleItems: []
    };

    if (!program) {
      return fallback;
    }

    if (this.state.programContent[program] === undefined) {
      void this.loadProgramContent(program);
    }
    return this.state.programContent[program] ?? fallback;
  };

  validateProgramSlug = (slug?: string): KnownProgram | false => {
    if (!slug) {
      return false;
    }
    const program = Object.values(KnownProgram).find(
      (p) => (p as string) === slug
    );
    return program ? program : false;
  };

  /**
   * select module item by status and/or taskReference
   * - first match is returned
   * - multiple selects work as "or"
   * - all options in one select work as "and"
   */
  selectProgramModule = (
    program?: KnownProgram,
    select: {
      status?: TaskResponse.status;
      taskReference?: string;
    }[] = []
  ): ProgramModuleItem | undefined => {
    const programModules = this.getProgramContent(program).moduleItems;
    if (!programModules) {
      return;
    }

    for (const moduleItem of programModules) {
      for (const selectItem of select) {
        let passedPrevious = true;

        // check for status match
        if (selectItem.status) {
          passedPrevious = moduleItem.status === selectItem.status;
        }

        // check for taskReference match
        if (selectItem.taskReference && passedPrevious) {
          passedPrevious =
            moduleItem.taskReference === selectItem.taskReference;
        }

        if (passedPrevious) {
          return moduleItem;
        }
      }
    }
  };

  // select a task from a module item to represent the overall status/state of the module
  static getModuleTaskRepresentative = (
    module?: ProgramModuleItem
  ):
    | {
        task: TaskResponseKnown;
        details: AppTaskDetails;
        allTasks: {
          task: TaskResponseKnown;
          details: AppTaskDetails;
        }[];
      }
    | undefined => {
    if (!module) {
      return;
    }

    // get the last task in the module
    const selectedTask = module.tasks?.[module.tasks.length - 1];
    if (!selectedTask) {
      return;
    }

    const taskManagementState = Blac.getBloc(TaskManagementBloc);
    const taskDetails = taskManagementState.getTaskDetails(selectedTask);

    return {
      task: selectedTask,
      details: taskDetails,
      allTasks:
        module.tasks?.map((task) => ({
          task,
          details: taskManagementState.getTaskDetails(task)
        })) ?? []
    };
  };
  getModuleTaskRepresentative = ProgramBloc.getModuleTaskRepresentative;

  // find first module item that has a task with status AVAILABLE
  findFirstModuleItemByTaskStatus = (
    program: KnownProgram,
    status: TaskResponse.status
  ): ProgramModuleItem | undefined => {
    const { moduleItems } = this.getProgramContent(program);
    if (!moduleItems) {
      return;
    }
    return moduleItems.find((item) => {
      let isAvailable = false;

      for (const task of item.tasks ?? []) {
        if (task.status === status) {
          isAvailable = true;
        }
      }

      return isAvailable;
    });
  };

  areAllModulesCompleted = (program: KnownProgram): boolean => {
    const tasks = Blac.getBloc(TaskManagementBloc).getProgramsTasks([program]);

    if (tasks.length === 0) {
      return false;
    }
    return tasks.every(
      (item) =>
        item.status === TaskResponse.status.COMPLETED ||
        item.status === TaskResponse.status.SKIPPED
    );
  };

  updateProgramStatus = (program: KnownProgram): ProgramStatus => {
    let status: ProgramStatus;

    if (this.areAllModulesCompleted(program)) {
      status = ProgramStatus.COMPLETED;
    } else {
      const tasks = Blac.getBloc(TaskManagementBloc).getProgramsTasks([
        program
      ]);
      const hasStarted = tasks.some(
        (item) =>
          item.status === TaskResponse.status.COMPLETED ||
          item.status === TaskResponse.status.SKIPPED ||
          item.status === TaskResponse.status.IN_PROGRESS
      );
      status = hasStarted ? ProgramStatus.IN_PROGRESS : ProgramStatus.AVAILABLE;
    }

    this.emit({
      ...this.state,
      programStatus: {
        ...this.state.programStatus,
        [program]: status
      }
    });

    return status;
  };

  // count how many module items have been completed by user
  countModuleItemsByStatus = (
    program: KnownProgram,
    status: TaskResponse.status
  ): number => {
    const { moduleItems } = this.getProgramContent(program);
    if (!moduleItems) {
      return 0;
    }
    return moduleItems.reduce((acc, item) => {
      return item.status === status ? acc + 1 : acc;
    }, 0);
  };

  checkUserProfileProgramData = async (
    refreshAttributes?: boolean
  ): Promise<void> => {
    if (refreshAttributes) {
      await userState.getProfileAttributes();
    }

    const program =
      await userState.checkProfileAttributesCurrentProgramMembership();

    const programKeyToKnownProgramMap: Record<
      ProfileProgramMembership,
      KnownProgram | null
    > = {
      [ProfileProgramMembership.MEDICARE]: null,
      [ProfileProgramMembership.A1C_ONLY]: null,
      [ProfileProgramMembership.ALLINONE_HEALTHCARE_CONCIERGE]:
        KnownProgram.GENERAL_ALL,
      [ProfileProgramMembership.CDCDPP]: KnownProgram.CDC_DPP,
      [ProfileProgramMembership.HEALTHY_WEIGHT_JOURNEY]:
        KnownProgram.PATH_HEALTHY_WEIGHT_HWJ_12W,
      [ProfileProgramMembership.DIABETES_AND_HEART_DISEASE_PREVENTION]:
        KnownProgram.DIABETES_PREVENTION,
      [ProfileProgramMembership.EVERYDAY_DIABETES_JOURNEY]:
        KnownProgram.EVERYDAY_DIABETES_JOURNEY
    };

    const knownProgram = program
      ? programKeyToKnownProgramMap[program[0]]
      : null;

    const userHasNoPrograms = knownProgram ? undefined : true;

    this.emit({
      ...this.state,
      autoProgramDetected: knownProgram ?? undefined,
      userHasNoPrograms
    });

    if (knownProgram) {
      await this.loadProgramContent(knownProgram, true);
      this.updateProgramStatus(knownProgram);
    }
  };
}
